/**
 * Manufacturing Dashboard Page
 * Main dashboard for manufacturing operations overview
 *
 * @description
 * Displays comprehensive manufacturing analytics including:
 * - Key performance indicators (KPIs)
 * - Transaction statistics
 * - Production metrics
 * - Payment summaries
 * - Raw material analytics
 * - Production and payment trends charts
 * - Quick action buttons
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-3
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText,
  Factory,
  IndianRupee,
  Package,
  RefreshCw,
  Plus,
  ListChecks,
  Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangeSelector, DateRange, DateRangePreset } from '@/components/batch/DateRangeSelector';
import { KPICard } from '@/components/manufacturing/dashboard/KPICard';
import { ProductionChart } from '@/components/manufacturing/dashboard/ProductionChart';
import { PaymentChart } from '@/components/manufacturing/dashboard/PaymentChart';
import { useDashboardKpis, useProductionChart, usePaymentChart } from '@/hooks/useDashboard';
import { CreateProductionBatchModal } from '@/components/manufacturing/modals/CreateProductionBatchModal';

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format number with thousand separators
 */
const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-IN').format(value);
};

/**
 * Format currency value
 */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value);
};

/**
 * Get date range based on preset
 * Calculates the correct date range for the given preset value
 */
const getDateRangeForPreset = (preset: DateRangePreset): DateRange => {
  const today = new Date();
  let from: Date;

  switch (preset) {
    case 'last7days':
      from = new Date(today);
      from.setDate(today.getDate() - 7);
      break;
    case 'last30days':
      from = new Date(today);
      from.setDate(today.getDate() - 30);
      break;
    case 'last90days':
      from = new Date(today);
      from.setDate(today.getDate() - 90);
      break;
    default:
      // Default to last 30 days
      from = new Date(today);
      from.setDate(today.getDate() - 30);
  }

  return { from, to: today };
};

// ============================================
// MAIN COMPONENT
// ============================================

export const ManufacturingDashboard: React.FC = () => {
  const navigate = useNavigate();

  // State management - persist filter selection in localStorage
  const FILTER_STORAGE_KEY = 'manufacturing_dashboard_filter';

  const getInitialFilter = (): { preset: DateRangePreset; range: DateRange } => {
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const preset = parsed.preset as DateRangePreset;
        if (preset === 'custom' && parsed.from && parsed.to) {
          return {
            preset,
            range: { from: new Date(parsed.from), to: new Date(parsed.to) }
          };
        }
        if (preset === 'last7days' || preset === 'last30days' || preset === 'last90days') {
          return { preset, range: getDateRangeForPreset(preset) };
        }
      }
    } catch {
      // Ignore parse errors, fall through to default
    }
    return { preset: 'last30days', range: getDateRangeForPreset('last30days') };
  };

  const initialFilter = getInitialFilter();
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>(initialFilter.preset);
  const [dateRange, setDateRange] = useState<DateRange>(initialFilter.range);
  const [createBatchModalOpen, setCreateBatchModalOpen] = useState(false);
  // Auto-select chart interval based on date range for readability
  const getChartInterval = (range: DateRange): 'DAILY' | 'WEEKLY' | 'MONTHLY' => {
    const diffDays = Math.ceil((range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 7) return 'DAILY';
    if (diffDays <= 90) return 'WEEKLY';
    return 'MONTHLY';
  };

  const chartInterval = getChartInterval(dateRange);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch dashboard data
  const { data: kpiData, isLoading: kpiLoading, refetch: refetchKpis } = useDashboardKpis(dateRange);
  const { data: productionData, isLoading: productionLoading, refetch: refetchProduction } = useProductionChart({
    ...dateRange,
    interval: chartInterval
  });
  const { data: paymentData, isLoading: paymentLoading, refetch: refetchPayment } = usePaymentChart({
    ...dateRange,
    interval: chartInterval
  });

  // Handlers
  const handleDateRangeChange = (preset: DateRangePreset, range: DateRange) => {
    setDateRangePreset(preset);
    setDateRange(range);
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        preset,
        from: range.from.toISOString(),
        to: range.to.toISOString()
      }));
    } catch {
      // Ignore storage errors
    }
  };

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const [kpiResult, productionResult, paymentResult] = await Promise.all([
        refetchKpis(),
        refetchProduction(),
        refetchPayment(),
      ]);

      const hasError =
        kpiResult.status === 'error' ||
        productionResult.status === 'error' ||
        paymentResult.status === 'error';

      if (hasError) {
        toast.error('Failed to refresh dashboard', {
          description: 'Some data could not be updated. Please try again.',
        });
      } else {
        toast.success('Dashboard refreshed successfully', {
          description: 'All dashboard data has been updated.',
        });
      }
    } catch {
      toast.error('Failed to refresh dashboard', {
        description: 'Please check your connection and try again.',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetchKpis, refetchProduction, refetchPayment]);

  return (
    <div className="space-y-6 p-6">
      {/* Header Section */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Manufacturing Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of manufacturing operations and key metrics
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <DateRangeSelector
            value={dateRangePreset}
            dateRange={dateRange}
            onChange={handleDateRangeChange}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh dashboard"
            tabIndex={0}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards Section */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Transactions KPI */}
        <KPICard
          title="Total Transactions"
          value={formatNumber(kpiData?.transactions.total || 0)}
          icon={FileText}
          subtitle={`${kpiData?.transactions.pendingQc || 0} pending QC`}
          badge={{
            text: `${kpiData?.transactions.readyForProduction || 0} ready`,
            variant: 'success'
          }}
          loading={kpiLoading}
        />

        {/* Production KPI */}
        <KPICard
          title="Production Batches"
          value={formatNumber(kpiData?.production.totalBatches || 0)}
          icon={Factory}
          subtitle={`${kpiData?.production.completed || 0} completed`}
          badge={{
            text: `${formatNumber(kpiData?.production.totalOutputKg || 0)} kg output`,
            variant: 'default'
          }}
          loading={kpiLoading}
        />

        {/* Payments KPI */}
        <KPICard
          title="Payments"
          value={formatCurrency(kpiData?.payments.totalAmountPaid || 0)}
          icon={IndianRupee}
          subtitle={`${kpiData?.payments.completed || 0} completed`}
          badge={{
            text: `${kpiData?.payments.pending || 0} pending`,
            variant: kpiData?.payments.pending ? 'warning' : 'default'
          }}
          loading={kpiLoading}
        />

        {/* Raw Materials KPI */}
        <KPICard
          title="Raw Materials"
          value={`${formatNumber(kpiData?.rawMaterials.totalReceivedKg || 0)} kg`}
          icon={Package}
          subtitle={`Avg Grade: ${kpiData?.rawMaterials.averageGrade || 'N/A'}`}
          loading={kpiLoading}
        />
      </div>

      {/* Charts Section */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Production Chart */}
        <ProductionChart
          data={productionData}
          loading={productionLoading}
        />

        {/* Payment Chart */}
        <PaymentChart
          data={paymentData}
          loading={paymentLoading}
        />
      </div>

      {/* Quick Actions and Grade Distribution Section */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Quick Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2">
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => navigate('/manufacturing/transactions/new')}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Transaction
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => setCreateBatchModalOpen(true)}
            >
              <Factory className="mr-2 h-4 w-4" />
              New Production Batch
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => navigate('/manufacturing/transactions?status=INITIALIZED')}
            >
              <ListChecks className="mr-2 h-4 w-4" />
              View Pending QC ({kpiData?.transactions.pendingQc || 0})
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => navigate('/manufacturing/payments?status=PENDING')}
            >
              <Wallet className="mr-2 h-4 w-4" />
              View Pending Payments ({kpiData?.payments.pending || 0})
            </Button>
          </CardContent>
        </Card>

        {/* Grade Distribution Card */}
        <Card>
          <CardHeader>
            <CardTitle>Grade Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {kpiLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex justify-between items-center">
                    <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                    <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                  </div>
                ))}
              </div>
            ) : kpiData?.rawMaterials.gradeDistribution &&
              Object.keys(kpiData.rawMaterials.gradeDistribution).length > 0 ? (
              <div className="space-y-3">
                {(() => {
                  const totalGraded = Object.values(kpiData.rawMaterials.gradeDistribution)
                    .reduce((sum, c) => sum + c, 0);
                  return Object.entries(kpiData.rawMaterials.gradeDistribution).map(([grade, count]) => (
                    <div key={grade} className="flex justify-between items-center">
                      <span className="text-sm font-medium">Grade {grade}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{
                              width: `${totalGraded > 0 ? (count / totalGraded) * 100 : 0}%`
                            }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {count}
                        </span>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No grade distribution data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Create Production Batch Modal */}
      <CreateProductionBatchModal
        open={createBatchModalOpen}
        onOpenChange={setCreateBatchModalOpen}
        onSuccess={() => {
          setCreateBatchModalOpen(false);
          refetchKpis();
        }}
      />
    </div>
  );
};

export default ManufacturingDashboard;
